import React, { useEffect, useRef } from 'react';
import Carousel from 'react-multi-carousel';
import Skeleton from 'react-loading-skeleton';
import { Box, Text, Container, Flex } from '@chakra-ui/react';
import { usePengetahuanContext } from '../../controller/pengetahuan/index';
import CardItem from '../global/Card/CardItem';
import { PiArrowLeft, PiArrowRight } from 'react-icons/pi';

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 3,
  },
  tablet: {
    breakpoint: { max: 1024, min: 560 },
    items: 2,
  },
  mobile: {
    breakpoint: { max: 560, min: 0 },
    items: 1,
  },
};

interface KnowledgeCenterProps {
  title: string;
  description: string;
  type: string;
  navigatePath: string;
}

interface KnowledgeCarouselProps {
  listData: any[];
  carouselRef: any;
}

const KnowledgeCarousel: React.FC<KnowledgeCarouselProps> = ({
  listData,
  carouselRef,
}) => {
  if (listData.length === 0) {
    return (
      <Box mx="auto" textAlign="center" my={20}>
        <Text fontSize="xl" color="gray.400">
          Belum Ada Pengetahuan Terbaru
        </Text>
      </Box>
    );
  }

  return (
    <Carousel responsive={responsive} ref={carouselRef} arrows={false}>
      {listData.map((v: any) => (
        <CardItem item={v} key={v.id} accessFor="pengetahuan" />
      ))}
    </Carousel>
  );
};

/**
 * KnowledgeCenter component.
 *
 * @returns {React.FC} The KnowledgeCenter component.
 * @author Bagas Prasetyadi
 */

const KnowledgeCenter: React.FC<KnowledgeCenterProps> = ({
  title,
  type,
  description,
  navigatePath,
}) => {
  const { isLoading, listData, listDataSync } = usePengetahuanContext().state;
  const { fetchData } = usePengetahuanContext();

  useEffect(() => {
    fetchData({ jenis_pengetahuan: type });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayedData = type === 'synchronized' ? listDataSync : listData;

  const carouselRef = useRef<any>(null);

  const handleNext = () => {
    carouselRef.current?.next();
  };

  const handlePrevious = () => {
    carouselRef.current?.previous();
  };

  return (
    <Box bg="white">
      <Container maxW="8xl" py={12} w="full">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          w="full"
        >
          <Flex direction="column">
            <Text
              as="h2"
              fontWeight="semibold"
              letterSpacing="tight"
              fontSize={{ base: 'xl', md: '2xl' }}
            >
              {title}
            </Text>
            <Text>{description}</Text>
          </Flex>

          <Flex direction="row" className="gap-2">
            <button
              onClick={handlePrevious}
              className="bg-white shadow-md w-10 h-10 flex items-center justify-center rounded-lg hover:shadow-lg hover:bg-gray-200"
            >
              <PiArrowLeft size={30} className="text-gray-500" />
            </button>
            <button
              onClick={handleNext}
              className="bg-white shadow-md w-10 h-10 flex items-center justify-center rounded-lg hover:shadow-lg hover:bg-gray-200"
            >
              <PiArrowRight size={30} className="text-gray-500" />
            </button>
          </Flex>
        </Box>
        <Box mt={5}>
          {isLoading ? (
            <Skeleton
              count={4}
              style={{ height: '400px', width: '280px', marginRight: '20px' }}
              inline={true}
            />
          ) : (
            <KnowledgeCarousel
              listData={displayedData}
              carouselRef={carouselRef}
            />
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default KnowledgeCenter;
